import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { preferredLanguage } from '../core/i18n/preferred-language';
import { ActivateAccountPage } from '../pages/activate-account-page';
import { ArticlePage } from '../pages/article-page';
import { BlogPage } from '../pages/blog-page';
import { BookingPage } from '../pages/booking-page';
import { HomePage } from '../pages/home-page';
import { NotFoundPage } from '../pages/not-found-page';
import { PolicyPage } from '../pages/policy-page';
import { RegisterPage } from '../pages/register-page';
import { PortalCasePage } from '../pages/portal-case-page';
import { PortalPage } from '../pages/portal-page';
import { AppointmentsTab } from '../pages/portal/appointments-tab';
import { CasesTab } from '../pages/portal/cases-tab';
import { MessagesTab } from '../pages/portal/messages-tab';
import { NotificationsTab } from '../pages/portal/notifications-tab';
import { ProfileTab } from '../pages/portal/profile-tab';
import { SignInPage } from '../pages/sign-in-page';
import { rememberedLanguage } from './language-memory';
import { LanguageLayout } from './language-layout';
import { RequireSession } from './require-session';
import { SiteChrome } from './site-chrome';

/**
 * Every route carries its language as a URL prefix, matching the live static site.
 *
 * **The unprefixed URLs are a contract, not a convenience.** `/book`, `/policy` and the `/legal`
 * alias are all answerable on the live site today, so they are in the wild and in people's history.
 * They redirect to their prefixed forms; they must never 404. The one thing they must also not do is
 * pick the wrong language on the way — see `preferredLanguage`.
 */
export function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<LanguageRedirect to="" />} />

            {/* The live site's unprefixed URLs, kept answerable. */}
            <Route path="/book" element={<LanguageRedirect to="/book" />} />
            <Route path="/policy" element={<LanguageRedirect to="/policy" />} />
            <Route path="/legal" element={<LanguageRedirect to="/policy" />} />
            <Route path="/blog" element={<LanguageRedirect to="/blog" />} />

            <Route path="/:lang" element={<LanguageLayout />}>
                {/*
                    Three pages sit outside the shared chrome because each carries its own, as it
                    does on the live site. The landing page's navigation is inside the hero, over
                    the gradient; the booking wizard's is a slim bar with one way out of the form
                    and no way to wander off it; the legal page's is the site bar without the
                    marketing links. Everything else is a child of `SiteChrome`.
                */}
                <Route index element={<HomePage />} />
                <Route path="book" element={<BookingPage />} />
                <Route path="policy" element={<PolicyPage />} />

                <Route element={<SiteChrome />}>
                    {/* `/legal` was an alias on the live site and stays one, under the prefix too. */}
                    <Route path="legal" element={<PolicyAlias />} />
                    <Route path="blog" element={<BlogPage />} />
                    {/*
                        The slug is per language, so this is genuinely two route trees that happen to
                        share a component — /kk/blog/<kazakh-slug> and /ru/blog/<russian-slug> resolve
                        different articles, and neither slug answers under the other prefix.
                    */}
                    <Route path="blog/:slug" element={<ArticlePage />} />
                    <Route path="account/sign-in" element={<SignInPage />} />
                    <Route path="account/register" element={<RegisterPage />} />
                    <Route path="account/activate" element={<ActivateAccountPage />} />

                    <Route element={<RequireSession />}>
                        {/*
                            The portal's sections are routes under one frame. Cases is the index so
                            every existing link to /{lang}/portal still lands where it did.
                        */}
                        <Route path="portal" element={<PortalPage />}>
                            <Route index element={<CasesTab />} />
                            <Route path="appointments" element={<AppointmentsTab />} />
                            <Route path="messages" element={<MessagesTab />} />
                            <Route path="notifications" element={<NotificationsTab />} />
                            <Route path="profile" element={<ProfileTab />} />
                        </Route>
                        <Route path="portal/cases/:clientCaseId" element={<PortalCasePage />} />
                    </Route>

                    <Route path="*" element={<NotFoundPage />} />
                </Route>
            </Route>
        </Routes>
    );
}

/**
 * Sends a visitor with no language in the URL to the one they read.
 *
 * The query string and hash travel with them: an activation link is `/account/activate?token=…`, and
 * a redirect that dropped the query would turn every invitation into a dead page — the defect
 * `switch-language-path` already had once, on the other half of the same problem.
 */
function LanguageRedirect({ to }: { to: string }) {
    const location = useLocation();
    const language = preferredLanguage(navigator.languages, rememberedLanguage());

    return <Navigate to={`/${language}${to}${location.search}${location.hash}`} replace />;
}

/** `/{lang}/legal` → `/{lang}/policy`, keeping one canonical URL for one document. */
function PolicyAlias() {
    const location = useLocation();

    return <Navigate to={location.pathname.replace(/\/legal$/, '/policy')} replace />;
}
